import {
    getFilledFailListItems,
    isAudioControlBlockActive,
    isAudioControlBlockCounted,
    isAudioControlConditionActive,
    isAudioControlConditionCounted,
    normalizeAudioControlBlocks,
    normalizeFailList,
    type AudioControlBlock,
    type AudioControlCondition,
    type AudioControlFailListItem,
    type QualityControlApiNode,
    type QualityControlBlockType,
} from '../../Settings/AudioControlList/audioControlQuestionnaire';

export type ManualControlCitationTagItem = {
    id?: number;
    text?: string;
    start?: number;
    speaker?: string;
    comment?: string;
};

export type ManualControlBlockElementPayload = {
    elementId: number;
    description: string;
    tag: string;
    isFailed: boolean;
    score: number;
};

export type ManualControlBlockPayload = {
    elementId: number;
    score: number;
    elements: ManualControlBlockElementPayload[];
};

export type ManualControlApiItem = {
    elementId: number;
    description: string;
    tag: string;
    status: number;
    score: number;
    isManual: boolean;
};

export type ControlResultApiItem = {
    id?: number | null;
    parentId?: number | null;
    qualityControlId?: number | null;
    orderId?: number | null;
    blockType?: string | null;
    name?: string | null;
    scenario?: string | null;
    weight?: number | null;
    failText?: string | null;
    isChecked?: boolean | null;
    isActive?: boolean | null;
    callDetailId?: number | null;
    elementId?: number | null;
    description?: string | null;
    tag?: string | null;
    status?: number | null;
    score?: number | null;
    elementScore?: number | null;
    isManual?: boolean | null;
    userId?: string | null;
    isFailed?: boolean | null;
    children?: ControlResultApiItem[];
    elements?: ControlResultApiItem[];
};

export type ControlResultEnvelope = {
    id?: number | null;
    status?: number | null;
    score?: number | null;
    isManual?: boolean | null;
    userId?: string | null;
    userFullName?: string | null;
    dateTimeStart?: string | null;
    dateTimeEnd?: string | null;
    branchId?: number | null;
    mainBranchId?: number | null;
    blocks?: ControlResultApiItem[];
    failList?: unknown;
    questionnaireFailConditions?: unknown;
};

export type ManualControlElementsMap = Record<number, ManualControlApiItem>;

export type ManualEvaluationCitation = ManualControlCitationTagItem;

export type ManualEvaluationBlockScoreMap = Record<number, number | null>;

export type ManualEvaluationState = {
    elements: ManualControlElementsMap;
    blockScores: ManualEvaluationBlockScoreMap;
};

export const MANUAL_CONTROL_ELEMENT_STATUS = {
    NO_VIOLATION: 102,
    VIOLATION: 101,
} as const;

export const VIOLATION_DESCRIPTION_MAX_LENGTH = 2000;
export const CITATION_COMMENT_MAX_LENGTH = 200;

const limitStoredTextLength = (value: string | undefined, maxLength: number) =>
    String(value ?? '').slice(0, maxLength);

export const pickElementId = (value: unknown): number | null => {
    if (value == null || value === '') {
        return null;
    }

    const parsed = Number(value);
    if (!Number.isInteger(parsed)) {
        return null;
    }

    return parsed;
};

const normalizeBlockScore = (passedWeight: number, totalWeight: number) => {
    if (totalWeight <= 0) {
        return 0;
    }

    const score = (passedWeight / totalWeight) * 100;
    if (score >= 99.95) {
        return 100;
    }

    return Math.round(score * 10) / 10;
};

export const isNoViolationStatus = (status: number | undefined) =>
    status === MANUAL_CONTROL_ELEMENT_STATUS.NO_VIOLATION;

export const calculateElementApiScore = (
    condition: Pick<AudioControlCondition, 'weight'>,
    status: number,
): number => {
    const weight = Number(condition.weight) || 0;

    if (isNoViolationStatus(status)) {
        return weight;
    }

    return 0;
};

export const createManualControlElement = (
    elementId: number,
    condition: Pick<AudioControlCondition, 'weight'>,
    patch: Partial<Omit<ManualControlApiItem, 'elementId'>> = {},
): ManualControlApiItem => {
    const status = patch.status ?? MANUAL_CONTROL_ELEMENT_STATUS.NO_VIOLATION;

    return {
        elementId,
        description: patch.description ?? '',
        tag: patch.tag ?? '',
        status,
        score: patch.score ?? calculateElementApiScore(condition, status),
        isManual: patch.isManual ?? true,
    };
};

export const serializeCitationsToTag = (citations: ManualEvaluationCitation[] | undefined): string => {
    if (!citations?.length) {
        return '';
    }

    return JSON.stringify(
        citations.map((citation) => {
            if (citation.id != null) {
                const payload: {id: number; comment?: string} = {
                    id: Number(citation.id),
                };

                const comment = limitStoredTextLength(citation.comment ?? '', CITATION_COMMENT_MAX_LENGTH);
                if (comment) {
                    payload.comment = comment;
                }

                return payload;
            }

            return {
                text: citation.text,
                start: citation.start,
                speaker: citation.speaker,
                comment: limitStoredTextLength(citation.comment ?? '', CITATION_COMMENT_MAX_LENGTH),
            };
        }),
    );
};

const normalizeCitationId = (value: unknown): number | undefined => {
    if (value == null || value === '') {
        return undefined;
    }

    const id = Number(value);
    return Number.isInteger(id) ? id : undefined;
};

const normalizeCitationStart = (value: unknown): number | undefined => {
    if (value == null || value === '') {
        return undefined;
    }

    const start = Number(value);
    return Number.isFinite(start) ? start : undefined;
};

export const parseCitationsFromTag = (tag: unknown): ManualEvaluationCitation[] => {
    if (typeof tag !== 'string' || !tag.trim()) {
        return [];
    }

    try {
        const parsed = JSON.parse(tag);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.reduce<ManualEvaluationCitation[]>((acc, citation) => {
            if (!citation || typeof citation !== 'object') {
                return acc;
            }

            const id = normalizeCitationId((citation as ManualEvaluationCitation).id);
            const text = String((citation as ManualEvaluationCitation).text ?? '').trim();
            const comment = String((citation as ManualEvaluationCitation).comment ?? '');

            if (id == null && !text) {
                return acc;
            }

            acc.push({
                id,
                text: text || undefined,
                start: normalizeCitationStart((citation as ManualEvaluationCitation).start),
                speaker: (citation as ManualEvaluationCitation).speaker,
                comment,
            });

            return acc;
        }, []);
    } catch {
        return [];
    }
};

export const hasEvaluationCitations = (elements: ManualControlElementsMap): boolean =>
    Object.values(elements).some((element) =>
        parseCitationsFromTag(element?.tag).some((citation) =>
            citation.id != null || Boolean(citation.text?.trim()),
        ),
    );

export const getAttachedCitationIdsForElement = (
    elements: ManualControlElementsMap,
    elementId: number | null | undefined,
): Set<number> => {
    if (elementId == null) {
        return new Set();
    }

    const attachedIds = new Set<number>();

    parseCitationsFromTag(elements[elementId]?.tag).forEach((citation) => {
        if (citation.id != null) {
            attachedIds.add(Number(citation.id));
        }
    });

    return attachedIds;
};

export const manualControlItemsToElementsMap = (items: unknown): ManualControlElementsMap => {
    if (!Array.isArray(items)) {
        return {};
    }

    return items.reduce<ManualControlElementsMap>((acc, rawItem) => {
        if (!rawItem || typeof rawItem !== 'object') {
            return acc;
        }

        const item = rawItem as ManualControlApiItem;
        const elementId = pickElementId(item.elementId);
        if (elementId == null) {
            return acc;
        }

        acc[elementId] = {
            elementId,
            description: String(item.description ?? ''),
            tag: String(item.tag ?? ''),
            status: Number(item.status) || MANUAL_CONTROL_ELEMENT_STATUS.NO_VIOLATION,
            score: Number(item.score) || 0,
            isManual: item.isManual ?? true,
        };

        return acc;
    }, {});
};

const flattenControlResultItems = (items: ControlResultApiItem[]): ControlResultApiItem[] => {
    const flatItems: ControlResultApiItem[] = [];

    items.forEach((item) => {
        flatItems.push(item);

        if (item.elements?.length) {
            flatItems.push(...flattenControlResultItems(item.elements));
        }

        if (item.children?.length) {
            flatItems.push(...flattenControlResultItems(item.children));
        }
    });

    return flatItems;
};

const resolveControlResultStatus = (item: ControlResultApiItem): number | null => {
    if (item.isFailed === true) {
        return MANUAL_CONTROL_ELEMENT_STATUS.VIOLATION;
    }

    if (item.isFailed === false) {
        return MANUAL_CONTROL_ELEMENT_STATUS.NO_VIOLATION;
    }

    if (item.status == null) {
        return null;
    }

    return Number(item.status);
};

export const isControlResultBlocksFormat = (value: unknown): value is ControlResultApiItem[] =>
    Array.isArray(value)
    && value.some((item) => item && typeof item === 'object' && 'blockType' in item);

export const isControlResultEnvelopeFormat = (value: unknown): value is ControlResultEnvelope[] =>
    Array.isArray(value)
    && value.some((item) => item && typeof item === 'object' && Array.isArray((item as ControlResultEnvelope).blocks));

export const isControlResultFormat = (value: unknown): boolean =>
    isControlResultBlocksFormat(value) || isControlResultEnvelopeFormat(value);

export const normalizeControlResultData = (value: unknown): {
    blocks: ControlResultApiItem[];
    envelope: ControlResultEnvelope | null;
} => {
    if (!Array.isArray(value) || value.length === 0) {
        return {blocks: [], envelope: null};
    }

    if (isControlResultEnvelopeFormat(value)) {
        const envelope = value[0] as ControlResultEnvelope;

        return {
            blocks: Array.isArray(envelope.blocks) ? envelope.blocks : [],
            envelope,
        };
    }

    if (isControlResultBlocksFormat(value)) {
        return {
            blocks: value,
            envelope: null,
        };
    }

    return {blocks: [], envelope: null};
};

export const isControlResultNotFound = (value: unknown): boolean =>
    Array.isArray(value) && value.length === 0;

export const hasControlResultData = (value: unknown): boolean => {
    if (isControlResultEnvelopeFormat(value)) {
        return true;
    }

    return isControlResultBlocksFormat(value) && value.length > 0;
};

const isControlResultElement = (item: ControlResultApiItem): boolean => {
    if (item.parentId != null) {
        return resolveControlResultStatus(item) != null
            || item.description != null
            || item.tag != null;
    }

    const blockType = String(item.blockType ?? '').toLowerCase();

    if (blockType === 'block') {
        return false;
    }

    if (blockType === 'element' || blockType === 'questionnaire') {
        return true;
    }

    return item.status != null || item.description != null || item.tag != null;
};

const resolveControlResultElementScore = (item: ControlResultApiItem, status: number): number => {
    if (!isNoViolationStatus(status)) {
        return 0;
    }

    const rawScore = item.elementScore ?? item.score;
    if (rawScore == null) {
        return 0;
    }

    return Number(rawScore) || 0;
};

const resolveControlResultElementId = (item: ControlResultApiItem): number | null => {
    const explicitElementId = pickElementId(item.elementId);
    if (explicitElementId != null) {
        return explicitElementId;
    }

    const blockType = String(item.blockType ?? '').toLowerCase();
    if (blockType === 'element' || blockType === 'questionnaire') {
        return pickElementId(item.id);
    }

    return null;
};

export const controlResultToElementsMap = (items: unknown): ManualControlElementsMap => {
    if (!Array.isArray(items)) {
        return {};
    }

    return flattenControlResultItems(items as ControlResultApiItem[]).reduce<ManualControlElementsMap>((acc, item) => {
        const status = resolveControlResultStatus(item);
        if (!isControlResultElement(item) || status == null) {
            return acc;
        }

        const elementId = resolveControlResultElementId(item);
        if (elementId == null) {
            return acc;
        }

        acc[elementId] = {
            elementId,
            description: String(item.description ?? ''),
            tag: String(item.tag ?? ''),
            status,
            score: resolveControlResultElementScore(item, status),
            isManual: item.isManual ?? true,
        };

        return acc;
    }, {});
};

const getControlResultChildren = (item: ControlResultApiItem): ControlResultApiItem[] => {
    if (item.children?.length) {
        return item.children;
    }

    if (item.elements?.length) {
        return item.elements;
    }

    return [];
};

const resolveControlResultBlockType = (item: ControlResultApiItem): QualityControlBlockType => {
    const blockType = String(item.blockType ?? '').toLowerCase();

    if (blockType === 'questionnaire') {
        return 'Questionnaire';
    }

    if (blockType === 'element') {
        return 'Element';
    }

    if (blockType === 'block' || getControlResultChildren(item).length > 0) {
        return 'Block';
    }

    return 'Element';
};

const mapControlResultNodeToApiNode = (item: ControlResultApiItem): QualityControlApiNode => ({
    id: pickElementId(item.elementId) ?? pickElementId(item.id),
    parentId: item.parentId,
    qualityControlId: item.qualityControlId,
    orderId: item.orderId,
    blockType: resolveControlResultBlockType(item),
    name: item.name ?? item.description ?? null,
    scenario: item.scenario,
    weight: item.weight,
    failText: item.failText,
    isChecked: item.isChecked,
    isActive: item.isActive,
    children: getControlResultChildren(item).map(mapControlResultNodeToApiNode),
});

export const controlResultToAudioControlBlocks = (items: ControlResultApiItem[]): AudioControlBlock[] => {
    const blockItems = items.filter((item) => resolveControlResultBlockType(item) === 'Block');
    const sourceItems = blockItems.length > 0 ? blockItems : items;

    return normalizeAudioControlBlocks(sourceItems.map(mapControlResultNodeToApiNode));
};

export const buildManualEvaluationStateFromControlResult = (
    items: unknown,
    blocks: AudioControlBlock[],
): ManualEvaluationState => {
    const elements = controlResultToElementsMap(items);
    const blockScores: ManualEvaluationBlockScoreMap = {};

    if (!Array.isArray(items)) {
        return {elements, blockScores};
    }

    const flatItems = flattenControlResultItems(items as ControlResultApiItem[]);

    blocks.forEach((block, blockIndex) => {
        const blockId = pickElementId(block.id);
        if (blockId == null) {
            return;
        }

        const blockResult = flatItems.find((item) => {
            if (String(item.blockType ?? '').toLowerCase() !== 'block') {
                return false;
            }

            return pickElementId(item.id) === blockId || pickElementId(item.elementId) === blockId;
        });

        if (blockResult?.score != null) {
            blockScores[blockIndex] = Number(blockResult.score);
        }
    });

    return {
        elements,
        blockScores,
    };
};

export const buildQuestionnaireFromControlResult = (data: unknown): {
    blocks: AudioControlBlock[];
    failList: AudioControlFailListItem[];
    envelope: ControlResultEnvelope | null;
    state: ManualEvaluationState;
} => {
    const {blocks: resultBlocks, envelope} = normalizeControlResultData(data);
    const blocks = controlResultToAudioControlBlocks(resultBlocks);

    return {
        blocks,
        failList: getFilledFailListItems(
            normalizeFailList(envelope?.failList ?? envelope?.questionnaireFailConditions),
        ),
        envelope,
        state: buildManualEvaluationStateFromControlResult(resultBlocks, blocks),
    };
};

export const calculateBlockInnerScoreFromElements = (
    block: AudioControlBlock,
    elements: ManualControlElementsMap,
) => {
    const conditions = block.conditions ?? [];
    let totalWeight = 0;
    let passedWeight = 0;

    conditions.forEach((condition) => {
        if (!isAudioControlConditionCounted(condition) || !isAudioControlConditionActive(condition)) {
            return;
        }

        const elementId = pickElementId(condition.id);
        if (elementId == null) {
            return;
        }

        const weight = Number(condition.weight) || 0;
        totalWeight += weight;

        if (isNoViolationStatus(elements[elementId]?.status)) {
            passedWeight += weight;
        }
    });

    return normalizeBlockScore(passedWeight, totalWeight);
};

export const calculateBlockScoresFromElements = (
    blocks: AudioControlBlock[],
    elements: ManualControlElementsMap,
): ManualEvaluationBlockScoreMap => {
    const blockScores: ManualEvaluationBlockScoreMap = {};

    blocks.forEach((block, blockIndex) => {
        if (!isAudioControlBlockCounted(block) || !isAudioControlBlockActive(block)) {
            return;
        }

        const hasEvaluatedElement = (block.conditions ?? []).some((condition) => {
            if (!isAudioControlConditionCounted(condition) || !isAudioControlConditionActive(condition)) {
                return false;
            }

            const elementId = pickElementId(condition.id);
            return elementId != null && elements[elementId] != null;
        });

        if (hasEvaluatedElement) {
            blockScores[blockIndex] = calculateBlockInnerScoreFromElements(block, elements);
        }
    });

    return blockScores;
};

export const buildManualEvaluationState = (
    items: unknown,
    blocks: AudioControlBlock[],
): ManualEvaluationState => {
    if (isControlResultFormat(items)) {
        const {blocks: controlResultBlocks} = normalizeControlResultData(items);
        return buildManualEvaluationStateFromControlResult(controlResultBlocks, blocks);
    }

    const elements = manualControlItemsToElementsMap(items);

    return {
        elements,
        blockScores: {},
    };
};

export const serializeManualControlBlock = (
    block: AudioControlBlock,
    elements: ManualControlElementsMap,
): ManualControlBlockPayload | null => {
    const blockElementId = pickElementId(block.id);
    if (blockElementId == null) {
        return null;
    }

    const blockElements: ManualControlBlockElementPayload[] = [];

    (block.conditions ?? []).forEach((condition) => {
        if (!isAudioControlConditionCounted(condition) || !isAudioControlConditionActive(condition)) {
            return;
        }

        const elementId = pickElementId(condition.id);
        if (elementId == null) {
            return;
        }

        const element = elements[elementId];
        if (!element) {
            return;
        }

        blockElements.push({
            elementId,
            description: limitStoredTextLength(element.description, VIOLATION_DESCRIPTION_MAX_LENGTH),
            tag: element.tag,
            isFailed: !isNoViolationStatus(element.status),
            score: Number(condition.weight) || Number(element.score) || 0,
        });
    });

    if (blockElements.length === 0) {
        return null;
    }

    return {
        elementId: blockElementId,
        score: calculateBlockInnerScoreFromElements(block, elements),
        elements: blockElements,
    };
};

export const serializeManualControlBlocks = (
    blocks: AudioControlBlock[],
    elements: ManualControlElementsMap,
    options?: {
        blockIndex?: number;
    },
): ManualControlBlockPayload[] => {
    const payloads: ManualControlBlockPayload[] = [];

    blocks.forEach((block, blockIndex) => {
        if (options?.blockIndex != null && options.blockIndex !== blockIndex) {
            return;
        }

        if (!isAudioControlBlockCounted(block) || !isAudioControlBlockActive(block)) {
            return;
        }

        const payload = serializeManualControlBlock(block, elements);
        if (payload) {
            payloads.push(payload);
        }
    });

    return payloads;
};

export const serializeManualControlItems = (
    blocks: AudioControlBlock[],
    elements: ManualControlElementsMap,
    options?: {
        blockIndex?: number;
        isManual?: boolean;
    },
): ManualControlApiItem[] => {
    const items: ManualControlApiItem[] = [];

    blocks.forEach((block, blockIndex) => {
        if (options?.blockIndex != null && options.blockIndex !== blockIndex) {
            return;
        }

        if (!isAudioControlBlockCounted(block) || !isAudioControlBlockActive(block)) {
            return;
        }

        (block.conditions ?? []).forEach((condition) => {
            if (!isAudioControlConditionCounted(condition) || !isAudioControlConditionActive(condition)) {
                return;
            }

            const elementId = pickElementId(condition.id);
            if (elementId == null) {
                return;
            }

            const element = elements[elementId];
            if (!element) {
                return;
            }

            items.push({
                elementId,
                description: limitStoredTextLength(element.description, VIOLATION_DESCRIPTION_MAX_LENGTH),
                tag: element.tag,
                status: element.status,
                score: element.score,
                isManual: options?.isManual ?? element.isManual ?? true,
            });
        });
    });

    return items;
};
